import {differenceInBusinessDays, parseISO} from "date-fns";
import {
    PAGE,
    PortalEnumNameToValue,
    PortalEnumValueToName,
    ReferralConsideration,
    SLA_BUSINESS_DAYS,
    SpecialReferralConsideration
} from "placement/const";
import {PlacementType, ReferralAuthorizedHours, ReferralEstimatedHours, ServiceType, TreatmentOffered} from 'referral/domain/types';
import {NavigationFilterValue} from './NavigationFilterService';
import {fullName, Person} from "../../core/domain";
import {ConversationSummary, OfferStaffingPlan} from '../../offer/domain/types';

export type ClientProviderPreference = {
    sfProviderId: string;
    clientPreference: string;
    preferenceReason: string;
};

export type OfferConversationSummary = ConversationSummary & {
    offerGuid: string;
};

export type Referral = {
    guid: string;
    caseNumber: string;
    status: string;
    clientName: string;
    clientAge: number;
    placementType: string;
    serviceLocations: Array<string>;
    serviceLocationsParaprofessional: Array<string>;
    serviceLocationsMidLevel: Array<string>;
    serviceLocationsHighLevel: Array<string>;
    serviceLocationsGroupCare: Array<string>;
    serviceTypes: Array<string>;
    location: string;
    requiredLanguage: string;
    daysInPlacement: number;
    priority: string;
    referralConsiderations: Array<string>;
    scheduleNotes: string;
    escalationLevel: string;
    matchedDate: number;
    completeDate: number;
    county: string;
    clientNotifiedDate: number;
    matchedOfferProviderName: string;
    matchedOffer?: ReferralOffer;
    providerSelected: string;
    serviceNotes: string;
    salesforceCaseUrl: string;
    clientSiblingReferralsAllProvider: SiblingReferral[];
    clientProviderPreferences: ClientProviderPreference[];
    offerConversationSummaries?: OfferConversationSummary[] | null;
} & ReferralEstimatedHours & ReferralAuthorizedHours & PsychotherapyAuthorizedHour;

export type PsychotherapyAuthorizedHour = {
    authTherapyTreatmentUnitsPer: string;
    authTherapyTreatmentUnitType: string;
    authTherapyTreatmentUnit: number;
}

export type SiblingReferral = {
    guid: string;
    clientName: string;
    siblingCaseNumber: string;
    siblingSfCaseId: string;
    siblingReferralGuid: string;
    clientReferralGuid: string;
}

export interface Offer {
    guid: string;
    id: string;
    name: string;
    city: string;
    distance: string;
    address: string;
    patientCountLast90days: number;
    scheduleOfferDate: Date;
    comments: string;
    offerHourPercent: number;
    directCareHours: number;
    directCareHoursEst: number;
    midHours: number;
    midHoursEst: number;
    highHours: number;
    highHoursEst: number;
    groupHours: number;
    groupHoursEst: number;
    dateHoursProvided: Date;
    provideAllEstimatedHours: boolean;
    status: string;
    submittedAt: string;
    submittedBy: string;
    provideServiceInRequiredLanguage: boolean;
    provideServicesInClinic: boolean
    satisfiedSla: boolean;
    providerSfId?: string;
    referralGuid?: string;
    distanceToClinic?: Distances;
    treatmentOffered: string,
    caregiverMediatedMidLevelHours: number;
    caregiverMediatedMidLevelHoursEst: number | null;
    caregiverMediatedHighLevelHours: number | null;
    caregiverMediatedHighLevelHoursEst: number;
    startDateCaregiverMediatedTreatment: string | null,
    startDatePractMediatedTreatment: string | null,
    psychotherapyOffer: number | null,
    serviceLocationType: string,
    serviceLocationParaprofessionalType: string,
    serviceLocationMidLevelType: string,
    serviceLocationHighLevelType: string,
    serviceLocationGroupCareType: string,
    draftStaffingPlan: OfferStaffingPlan | null;
    declinedReason: string;
    conversationSummary?: ConversationSummary | null;
}

export const extractPsychotherapyAuthorizedHours = (referral: Referral) => {
    const isPsychotherapyReferral = referral.serviceTypes.some((st) => [
        ServiceType.Psychotherapy,
        ServiceType.Compass_Psychotherapy
    ].includes(PortalEnumValueToName.get(st) as ServiceType));
    if (!isPsychotherapyReferral) return null;
    return {
        authTherapyTreatmentUnit: referral.authTherapyTreatmentUnit,
        authTherapyTreatmentUnitType: referral.authTherapyTreatmentUnitType,
        authTherapyTreatmentUnitsPer: referral.authTherapyTreatmentUnitsPer
    }
}


export enum OfferStatus {
    ACCEPTED = 'ACCEPTED',
    CANCELLED = 'CANCELLED',
    DECLINED = 'DECLINED',
    REJECTED = 'REJECTED',
    SUBMITTED = 'SUBMITTED'
}

const shouldUseFirstServiceDateForSLA = (referral: Referral) => {
    return referral.serviceTypes.some((val) => [
        ServiceType.ThreeTierOrParentTraining,
        ServiceType.ThreeTierOrParentLed].includes(PortalEnumValueToName.get(val) as ServiceType)) && (
        PortalEnumValueToName.get(referral.placementType) === PlacementType.Treatment_Only
        || PortalEnumValueToName.get(referral.placementType) === PlacementType.Assessment_with_Concurrent_Treatment);
}

export function ReferralOfferToOffer(referralOffer: ReferralOffer, referral: Referral): Offer {
    let address = referralOffer.otherClinicLocation || '';
    if (referralOffer.clinic) {
        address = `${referralOffer.clinic.street}, ${referralOffer.clinic.city}, ${referralOffer.clinic.stateCode} ${referralOffer.clinic.zipCode}`;
    }
    const city = referralOffer.clinic?.city ?? '';

    let businessDays: number;
    // BHPNPP-1064
    if (shouldUseFirstServiceDateForSLA(referral)) {
        businessDays = (referralOffer.startDateCaregiverMediatedTreatment && referralOffer.submittedAt)
            ? differenceInBusinessDays(parseISO(referralOffer.startDateCaregiverMediatedTreatment), parseISO(referralOffer.submittedAt))
            : -1;
        if (referralOffer.treatmentOffered === TreatmentOffered.Practitioner_Mediated) {
            businessDays = (referralOffer.startDatePractMediatedTreatment && referralOffer.submittedAt)
                ? differenceInBusinessDays(parseISO(referralOffer.startDatePractMediatedTreatment), parseISO(referralOffer.submittedAt))
                : -1;
        }
    } else {
        businessDays = (referralOffer.firstServiceDate && referralOffer.submittedAt)
            ? differenceInBusinessDays(parseISO(referralOffer.firstServiceDate), parseISO(referralOffer.submittedAt))
            : -1;
    }

    const satisfiedSla = businessDays >= 0 && businessDays <= SLA_BUSINESS_DAYS;

    return {
        provideServicesInClinic: referralOffer.provideServicesInClinic,
        patientCountLast90days: referralOffer.provider.numOfAcceptedOffers,
        name: referralOffer.provider.name,
        id: referralOffer.id,
        guid: referralOffer.guid,
        city: city,
        distance: '',
        address: address,
        scheduleOfferDate: parseISO(referralOffer.firstServiceDate),
        dateHoursProvided: parseISO(referralOffer.lastServiceDate),
        comments: referralOffer.comments,
        offerHourPercent: referralOffer.offerHourPercentage,
        provideAllEstimatedHours: referralOffer.provideAllEstimatedHours,
        directCareHours: referralOffer.directCareHours,
        midHours: referralOffer.midLevelSupervisionHours,
        highHours: referralOffer.highLevelSupervisionHours,
        groupHours: referralOffer.groupCareHours,

        // BHPNPP-1301
        directCareHoursEst: referral.practDirectBeneficialHours,
        midHoursEst: referral.practMidBeneficialHours,
        highHoursEst: referral.practHighBeneficialHours,
        groupHoursEst: referral.practGroupBeneficialHours,
        caregiverMediatedMidLevelHoursEst:  referral.careMediatedMidMinHours ??  referral.careMediatedMidHours,
        caregiverMediatedHighLevelHoursEst: referral.careMediatedHighMinHours ?? referral.careMediatedHighHours,

        // BHPNPP-1043
        submittedBy: fullName(referralOffer.submittedBy),
        submittedAt: referralOffer.submittedAt,
        provideServiceInRequiredLanguage: referralOffer.provideServiceInRequiredLanguage,

        status: referralOffer.status,

        // BHPNPP-1064
        satisfiedSla,

        // BHPNPP-1103
        providerSfId: referralOffer?.provider.sfId,
        // BHPNPP-1067
        referralGuid: referralOffer?.referralGuid,

        // BHPNPP-459
        distanceToClinic: referralOffer?.distanceToClinic,
        treatmentOffered: referralOffer?.treatmentOffered,
        caregiverMediatedMidLevelHours: referralOffer?.caregiverMediatedMidLevelHours,
        caregiverMediatedHighLevelHours: referralOffer?.caregiverMediatedHighLevelHours,
        startDateCaregiverMediatedTreatment: referralOffer?.startDateCaregiverMediatedTreatment,
        startDatePractMediatedTreatment: referralOffer?.startDatePractMediatedTreatment,
        serviceLocationType: referralOffer?.serviceLocationType,
        psychotherapyOffer: referralOffer?.psychotherapyOffer,
        serviceLocationParaprofessionalType: referralOffer?.serviceLocationParaprofessionalType,
        serviceLocationMidLevelType: referralOffer?.serviceLocationMidLevelType,
        serviceLocationHighLevelType: referralOffer?.serviceLocationHighLevelType,
        serviceLocationGroupCareType: referralOffer?.serviceLocationGroupCareType,
        draftStaffingPlan: referralOffer?.draftStaffingPlan,

        declinedReason: referralOffer?.declinedReason,
        conversationSummary: referralOffer?.conversationSummary,
    };
}

export interface Clinic {
    location: string;
    street: string;
    city: string;
    stateCode: string;
    zipCode: string;
    longitude: number;
    latitude: number;
}

export interface ReferralOffer {
    id: string;
    guid: string;
    firstServiceDate: string;
    lastServiceDate: string;
    comments: string;
    directCareHours: number;
    midLevelSupervisionHours: number;
    highLevelSupervisionHours: number;
    groupCareHours: number;
    clinic: Clinic;
    provideServicesInClinic: boolean
    otherClinicLocation?: string;
    provider: Provider;
    referral: Referral;
    offerHourPercentage: number;
    provideAllEstimatedHours: boolean;
    status: string;
    submittedAt: string;
    submittedBy: Person;
    provideServiceInRequiredLanguage: boolean;
    referralGuid: string;
    distanceToClinic: Distances;
    treatmentOffered: string,
    caregiverMediatedMidLevelHours: number,
    caregiverMediatedHighLevelHours: number,
    psychotherapyOffer: number,
    startDateCaregiverMediatedTreatment: string,
    startDatePractMediatedTreatment: string,
    serviceLocationType: string,
    serviceLocationParaprofessionalType: string,
    serviceLocationMidLevelType: string,
    serviceLocationHighLevelType: string,
    serviceLocationGroupCareType: string,
    draftStaffingPlan: OfferStaffingPlan | null;
    declinedReason: string;
    conversationSummary?: ConversationSummary | null;
}

export function OpenForPlacementOffers(offers: Array<Offer>): Array<Offer> {
    return offers.filter((offer) => [OfferStatus.SUBMITTED, OfferStatus.ACCEPTED].includes(offer.status as OfferStatus));
}

export function MatchedProviderOffers(offers: Array<Offer>): Array<Offer> {
    // since we display sibling referrals in the same Matched Provider page
    // { accepted, rejected } for selected & matched referral
    // { submitted } for other sibling referrals, i.e. when selecting a sibling & unmatched referral, there are offers to display
    return offers.filter((offer) => [OfferStatus.SUBMITTED, OfferStatus.ACCEPTED, OfferStatus.REJECTED].includes(offer.status as OfferStatus));
}

export function CompleteReferralOffers(offers: Array<Offer>): Array<Offer> {
    return offers.filter((offer) => [OfferStatus.SUBMITTED, OfferStatus.ACCEPTED, OfferStatus.DECLINED, OfferStatus.REJECTED].includes(offer.status as OfferStatus));
}

export interface Provider {
    guid: string;
    name: string;
    sfId: string;
    numOfAcceptedOffers: number;
}

export interface ReferralColumn {
    name: string;
    attr: keyof Referral;
}

export function portalReferralParse(referral: Referral): Referral {
    var newReferral = {...referral};
    newReferral.referralConsiderations = [];
    if (referral.referralConsiderations) {
        newReferral.referralConsiderations = referral.referralConsiderations.map((val) => PortalEnumNameToValue.get(val)!);
    }
    newReferral.placementType = PortalEnumNameToValue.get(referral.placementType)!;

    newReferral.serviceTypes = [];
    if (referral.serviceTypes) {
        newReferral.serviceTypes = referral.serviceTypes.map((val) => {
            return PortalEnumNameToValue.get(val)!;
        });
    }

    if (referral.matchedOffer && referral.matchedOffer.provider) {
        newReferral.matchedOfferProviderName = referral.matchedOffer.provider.name;
    } else if (referral.providerSelected) {
        newReferral.matchedOfferProviderName = newReferral.providerSelected;
    }


    return newReferral;
}

export function specialReferralConsiderations(referral: Referral): Array<string> {
    const considerations = referral.referralConsiderations;
    let specialReferralConsiderations = considerations.reduce((res, consideration) => {
        if (Object.values(SpecialReferralConsideration).includes(consideration as SpecialReferralConsideration)) {
            res.push(consideration);
        }
        return res;
    }, Array<string>());

    if (considerations.filter((consideration) =>
        (Object.values(ReferralConsideration).includes(consideration as ReferralConsideration))).length > specialReferralConsiderations.length) {
        specialReferralConsiderations.push(SpecialReferralConsideration.ProviderPreference);
    }

    return [...new Set(specialReferralConsiderations)];
}

export enum SortOrder {
    DESC = 'desc',
    ASC = 'asc'
}

export interface Distances {
    distanceInMiles: number;
    timeInMillis: number;
}

export interface SortBy {
    column: keyof Referral | 'matchedOffer.provider.name';
    order: SortOrder;
}

export interface Pagination {
    startCursor: string;
    endCursor: string;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    total: number;
    pageSize: number
}

export type PlacementHistoryState = {
    referralList: ReferralListState;
}

export type ReferralListState = {
    pagination: Pagination;
    order: SortBy;
    filter: NavigationFilterValue | undefined;
    currentCursor: string | null;
    page: PAGE;
}
